import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import WebsiteBuilder from './WebsiteBuilder';
import TemplateGallery from './TemplateGallery';
import WebsiteOnboardingWizard from './WebsiteOnboardingWizard';

/**
 * Entry point for `/settings/website`. Routes between three states based on
 * the tenant's onboarding progress:
 *
 *   publicSite.templateId absent           → <TemplateGallery />
 *   templateId set, onboardingCompleted absent → <WebsiteOnboardingWizard />
 *   both set                               → <WebsiteBuilder />
 *
 * A `?force=gallery` query param overrides the router so the "تغيير القالب"
 * button in SiteSettings can jump the user back to the gallery.
 */
const WebsiteRouter = () => {
  const { tenant, fetchTenant, loading: tenantLoading } = useTenantBranding();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const forced = searchParams.get('force');

  // One-shot re-fetch whenever we land here so the router reads the latest
  // publicSite state (avoids a stale cached tenant sticking the user on the
  // wrong step after select-template / complete-onboarding).
  const [refreshing, setRefreshing] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setRefreshing(true);
    fetchTenant().finally(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
    // fetchTenant is stable from context; re-run on path change so the router
    // always has fresh data when the user navigates back to /settings/website.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const step = useMemo(() => {
    if (forced === 'gallery') return 'gallery';
    if (forced === 'wizard' && tenant?.publicSite?.templateId) return 'wizard';
    if (!tenant?.publicSite?.templateId) return 'gallery';
    if (!tenant?.publicSite?.onboardingCompletedAt) return 'wizard';
    return 'builder';
  }, [forced, tenant]);

  if (tenantLoading || refreshing) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" dir="rtl">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (step === 'gallery') return <TemplateGallery />;
  if (step === 'wizard') return <WebsiteOnboardingWizard templateId={tenant?.publicSite?.templateId} />;
  return <WebsiteBuilder />;
};

export default WebsiteRouter;
